import { Modal, message } from 'antd';
import { pujaStyleTemplatesApi } from '../services/api';
import {
  filterTemplatesByThemeMode,
  getTemplateThemeMode,
  type StyleTemplateThemeMode,
} from './styleTemplateTheme';

type TemplateRow = { id: number; name: string; isActive?: boolean; themeMode?: StyleTemplateThemeMode };

export type DeleteStyleTemplateOptions = {
  templateId: number;
  templates: TemplateRow[];
  activeLiteStyleTemplateId?: number | null;
  activeDarkStyleTemplateId?: number | null;
  /** @deprecated Use activeLiteStyleTemplateId */
  activeStyleTemplateId?: number | null;
  onDeleted?: () => Promise<void> | void;
};

function resolveActiveLiteId(options: DeleteStyleTemplateOptions): number | null {
  const lite = options.activeLiteStyleTemplateId ?? options.activeStyleTemplateId;
  return lite != null ? Number(lite) : null;
}

function resolveActiveDarkId(options: DeleteStyleTemplateOptions): number | null {
  return options.activeDarkStyleTemplateId != null ? Number(options.activeDarkStyleTemplateId) : null;
}

function pickFallbackTemplate(templates: TemplateRow[], mode: StyleTemplateThemeMode): TemplateRow | undefined {
  const sameMode = filterTemplatesByThemeMode(templates, mode);
  return sameMode[0] ?? templates[0];
}

/** Delete a template after UI confirmation (Popconfirm, etc.). */
export async function deleteStyleTemplate(options: DeleteStyleTemplateOptions): Promise<void> {
  const { templateId, templates, onDeleted } = options;

  if (templates.length <= 1) {
    message.warning('At least one template must remain');
    return;
  }

  const target = templates.find((t) => Number(t.id) === Number(templateId));
  if (!target) {
    message.error('Template not found');
    return;
  }

  const remaining = templates.filter((t) => Number(t.id) !== Number(templateId));
  const activeLiteId = resolveActiveLiteId(options);
  const activeDarkId = resolveActiveDarkId(options);

  try {
    if (activeLiteId != null && Number(activeLiteId) === Number(templateId)) {
      const fallback = pickFallbackTemplate(remaining, 'lite');
      if (fallback?.id != null) {
        await pujaStyleTemplatesApi.update(fallback.id, { isActive: true, activeFor: 'lite' });
      }
    }
    if (activeDarkId != null && Number(activeDarkId) === Number(templateId)) {
      const fallback = pickFallbackTemplate(remaining, 'dark');
      if (fallback?.id != null) {
        await pujaStyleTemplatesApi.update(fallback.id, { isActive: true, activeFor: 'dark' });
      }
    }
    await pujaStyleTemplatesApi.delete(templateId);
    await onDeleted?.();
    message.success('Template deleted');
  } catch {
    message.error('Failed to delete template');
    throw new Error('Failed to delete template');
  }
}

/** Show a confirmation modal, then delete the template. */
export function confirmDeleteStyleTemplate(options: DeleteStyleTemplateOptions): Promise<void> {
  const { templateId, templates } = options;

  if (templates.length <= 1) {
    message.warning('At least one template must remain');
    return Promise.resolve();
  }

  const target = templates.find((t) => Number(t.id) === Number(templateId));
  if (!target) return Promise.resolve();

  return new Promise<void>((resolve) => {
    Modal.confirm({
      title: `Delete template “${target.name}”?`,
      content: 'This cannot be undone. Book-specific overrides in this template will be lost.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () =>
        deleteStyleTemplate(options)
          .then(() => resolve())
          .catch(() => resolve()),
      onCancel: () => resolve(),
    });
  });
}

export function buildSetActiveTemplatePayload(mode: StyleTemplateThemeMode) {
  return { isActive: true as const, activeFor: mode };
}

export { getTemplateThemeMode };
